import React, { useState } from "react";
import { Link } from "react-router-dom";
import {ChevronDown, ChevronUp} from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: "How Much Does Drag Racing Insurance Cost?",
      answer:
        "Drag racing insurance cost varies based on several factors including vehicle value, racing class, coverage limits, and your racing history. However, specialized drag race car insurance is often more affordable than you might think. Many racers find that our policies cost less than standard high-performance auto insurance because we understand the controlled environment of sanctioned drag racing. Typical annual premiums range from $800 to $3,500 depending on your specific needs. Contact us for a personalized quote based on your vehicle and racing activities.",
    },
    {
      question: "What Does Drag Racers Insurance Cover?",
      answer:
        "Our drag racers insurance provides comprehensive coverage including liability protection during sanctioned events, physical damage to your vehicle (both on and off track), off-track storage coverage, transport and trailer protection, and spare parts/equipment coverage. We cover the racer, not just the car, protecting you from bodily injury and property damage liability while racing.",
    },
    {
      question: "Does My Regular Auto Insurance Cover Drag Racing?",
      answer:
        "No, standard auto insurance policies specifically exclude coverage for racing activities. If you're involved in an incident at a drag strip, your regular car insurance won't provide protection. That's why you need specialized insurance for drag racing cars that's designed specifically for motorsports activities.",
    },
    {
      question:
        "What's the Difference Between Track Insurance and Full Coverage?",
      answer:
        "Track-only insurance covers you only during racing events at sanctioned facilities. Full drag car insurance coverage extends protection to transport, off-track storage, and even some street driving (depending on the policy). Most serious racers opt for comprehensive coverage to protect their high-value vehicles year-round.",
    },
    {
      question: "Are Modified Vehicles Covered?",
      answer:
        "Yes! Unlike standard insurers, we specialize in covering high-value modified drag racing vehicles. We offer agreed value policies that recognize the investment you've made in engine modifications, suspension upgrades, safety equipment, and custom fabrication. Your modifications won't void your coverage.",
    },
    {
      question: "What Racing Organizations Do You Cover?",
      answer:
        "We provide drag race car insurance for events sanctioned by major organizations including NHRA (National Hot Rod Association), IHRA (International Hot Rod Association), NMCA, NMRA, and most local racing associations. Coverage applies to bracket racing, heads-up racing, and various racing classes from street to pro mod.",
    },
    {
      question: "Is There a Deductible?",
      answer:
        "Yes, most policies include a deductible that you choose when setting up your coverage. Common deductible options range from $500 to $2,500. Higher deductibles typically result in lower premiums. We'll help you select a deductible that balances affordability with protection.",
    },
    {
      question: "How Quickly Can I Get Coverage?",
      answer:
        "Fast! Once we have your vehicle information and racing details, we can often provide a quote within hours and bind coverage the same day. Many racers get their drag racers insurance certificate within 24 hours, so you can race with confidence at your next event.",
    },
    {
      question: "What Information Do I Need to Get a Quote?",
      answer:
        "To provide an accurate drag racing insurance cost estimate, we'll need: vehicle year/make/model, current value (including modifications), racing class, sanctioning body, estimated number of events per year, and your racing experience. Having recent photos of your vehicle helps expedite the process.",
    },
    {
      question: "Does Coverage Include Practice Days and Test Sessions?",
      answer:
        "Yes, most policies include coverage during practice sessions, test days, and qualifying runs at sanctioned facilities. Some track day and time trial events may also be covered. We'll clearly outline what's included in your specific policy.",
    },
    {
      question: "Can I Add My Trailer to the Policy?",
      answer:
        "Absolutely! We offer trailer coverage as part of our comprehensive insurance for drag racing cars. This includes physical damage to the trailer, coverage for equipment stored in the trailer, and liability protection while towing.",
    },
    {
      question: "What Happens If My Vehicle Is Totaled?",
      answer:
        "With our agreed value policies, if your vehicle is deemed a total loss, you receive the full agreed-upon value without depreciation deductions. This is crucial for modified vehicles where you've invested significantly beyond the base vehicle value. We understand that your drag car isn't just transportation—it's a serious investment.",
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1a1a1a] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-[#DC0000]">Drag Racing Insurance</span>{" "}
              Frequently Asked Questions
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Everything you need to know about drag racing insurance cost,
              coverage options, and how to protect your high-value vehicle.
            </p>
          </div>
        </div>
      </section>

      {/* Cost Highlight Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#DC0000] rounded-lg p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How Much Does Drag Racing Insurance Cost?
            </h2>
            <p className="text-xl text-white/90 mb-6">
              Most racers are surprised to learn that specialized drag race car
              insurance is often more affordable than standard high-performance
              auto policies.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto text-white">
              <div>
                <div className="text-4xl font-bold mb-2">$800-$1,500</div>
                <div className="text-white/90">Street/Bracket Racers</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">$1,500-$2,500</div>
                <div className="text-white/90">Sportsman Classes</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">$2,500-$3,500+</div>
                <div className="text-white/90">Pro/High-Value Builds</div>
              </div>
            </div>
            <p className="text-white/90 mt-6 text-sm">
              *Actual drag racing insurance cost depends on vehicle value,
              coverage limits, and racing history
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-black border border-gray-800 rounded-lg overflow-hidden hover:border-[#DC0000] transition-colors"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left"
                >
                  <h3 className="text-lg md:text-xl font-semibold pr-8">
                    {faq.question}
                  </h3>
                  {openIndex === index ? (
                    <ChevronUp className="w-6 h-6 text-[#DC0000] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#1a1a1a] p-8 rounded-lg border border-gray-800">
              <h3 className="text-2xl font-bold mb-4">
                Why Specialized Coverage Costs Less
              </h3>
              <p className="text-gray-300 mb-4">
                Many racers assume that insurance for drag racing cars is
                prohibitively expensive, but the opposite is often true.
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">✓</span>
                  Controlled racing environment vs. street driving
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">✓</span>
                  Experienced, safety-conscious drivers
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">✓</span>
                  Sanctioned events with safety protocols
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">✓</span>
                  Limited annual usage compared to daily drivers
                </li>
              </ul>
            </div>

            <div className="bg-[#1a1a1a] p-8 rounded-lg border border-gray-800">
              <h3 className="text-2xl font-bold mb-4">
                Factors That Affect Your Rate
              </h3>
              <p className="text-gray-300 mb-4">
                Understanding what influences drag racing insurance cost helps you
                get the best rate:
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">•</span>
                  Agreed value of your vehicle
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">•</span>
                  Racing class and speed
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">•</span>
                  Number of events per year
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">•</span>
                  Coverage limits selected
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">•</span>
                  Deductible amount
                </li>
                <li className="flex items-start">
                  <span className="text-[#DC0000] mr-2">•</span>
                  Your racing experience and claims history
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DC0000]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white">
            Still Have Questions?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get personalized answers and a custom drag racing insurance cost
            quote tailored to your specific vehicle and racing activities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-black text-white px-8 py-4 rounded-md font-semibold text-lg hover:bg-gray-900 transition-all transform hover:scale-105"
            >
              Get Your Quote
            </Link>
            <a
              href="tel:1-800-DRAG-CAR"
              className="inline-block bg-white text-black px-8 py-4 rounded-md font-semibold text-lg hover:bg-gray-200 transition-all"
            >
              Call 1-800-DRAG-CAR
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
